import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  Button,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import "./OffersAvailableModal.css";
import { OfferField } from "../ServiceTabDetails.interface";

interface Offer {
  couponCode: string;
  description: string;
  longDescription?: string;
  couponExpirydate?: string;
  selected?: boolean;
}

interface OffersAvailableModalProps {
  field: OfferField;
  offers: Offer[];
  onClose: () => void;
  onApply: (code: string) => void;
  isOpen: boolean;
  onApplyOffer?: (code: string) => Promise<void>;
}

const OffersAvailableModal: React.FC<OffersAvailableModalProps> = ({
  field,
  offers,
  onClose,
  onApply,
  onApplyOffer,
  isOpen,
}) => {
  const theme = useTheme();

  const {
    offerModalTitle,
    offerModalSubTitle,
    offerModalSelectedLabel,
    offerModalApplyCTA,
  } = field.offerModal || {};

  const handleApplyClick = async (code: string) => {
    if (onApplyOffer) {
      await onApplyOffer(code);
    }
    onApply(code);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: { maxWidth: 700 },
      }}
    >
      <DialogTitle
        sx={{
          position: "relative",
          textAlign: "center",
        }}
      >
        <Typography
          variant="h5"
          className="offer-modal-title"
          sx={{ fontWeight: 700, textAlign: "center", width: "100%" }}
        >
          {offerModalTitle.replace("[X]", offers.length.toString())}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            paddingInlineEnd: 0,
            right: 12,
            top: 12,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ mb: 2, textAlign: "center", color: theme.palette.text.primary }}
        >
          {offerModalSubTitle}
        </Typography>
        <Box>
          {offers.map((offer) => (
            <Box
              key={offer.couponCode}
              className={`offer-card${offer.selected ? " selected" : ""}`}
              sx={{
                borderColor: offer.selected
                  ? theme.palette.primary.main
                  : theme.palette.border?.primary || "#e0e0e0",
                background: offer.selected
                  ? theme.palette.primary.light
                  : "#fff",
              }}
            >
              <Box>
                <span
                  className="offers-code"
                  style={{
                    color: theme.palette.primary.main,
                  }}
                >
                  {offer.couponCode}
                </span>
                {offer.description && (
                  <>
                    {" - "}
                    <span
                      className="offer-description"
                      style={{ color: theme.palette.primary.main }}
                    >
                      {offer.description}
                    </span>
                  </>
                )}
                {offer.couponExpirydate && (
                  <span
                    className="offer-expiry"
                    style={{
                      color: theme.palette.primary.main,
                    }}
                  >
                    Expires {offer.couponExpirydate}
                  </span>
                )}
              </Box>
              {offer.selected ? (
                <span
                  className="selected-pill"
                  style={{
                    color: theme.palette.primary.main,
                    background: "#fff",
                  }}
                >
                  <CheckCircleIcon
                    color="success"
                    fontSize="small"
                    style={{ marginRight: 4 }}
                  />
                  {offerModalSelectedLabel}
                </span>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  id="apply-offer-btn"
                  onClick={() => handleApplyClick(offer.couponCode)}
                  sx={{
                    background: theme.palette.primary.main,
                    color: "#fff",
                    "&:hover": {
                      background: theme.palette.primary.dark,
                    },
                  }}
                >
                  {offerModalApplyCTA}
                </Button>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OffersAvailableModal;
